import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography, Row, Col, Button, Tag, Spin, Alert } from "antd";
import {
  CloseCircleOutlined,
  ExportOutlined,
  StopOutlined,
  WarningOutlined,
  ReloadOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { regretDashboardSaga } from "./saga";
import { reducer, sliceKey, actions } from "./slice"; 
import { 
  selectStats, 
  selectCommonReasons, 
  selectKpis, 
  selectLoading, 
  selectError 
} from "./selectors";

const { Title, Text } = Typography;

const iconMap = {
  "Regret Rate": <CloseCircleOutlined />,
  "Regret Only (No Action Taken)": <WarningOutlined />,
  "Regret + Replacement Created (Mapped)": <ReloadOutlined />,
  "Regret + Replacement Fresh Order (Unmapped)": <PlusOutlined />,
  "Not Found Cases (Marked by Sampler)": <SearchOutlined />,
  "Rejected by Lab": <StopOutlined />
};

const RegretDashboard = ({ 
  filters = {},
  onDetailedClick = () => {},
}) => {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: regretDashboardSaga });
  
  const dispatch = useDispatch();
  const stats = useSelector(selectStats);
  const commonReasons = useSelector(selectCommonReasons);
  const kpis = useSelector(selectKpis);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);

  const filtersString = JSON.stringify(filters);

  useEffect(() => {
    dispatch(actions.fetchRegretData({ filtersString }));
  }, [dispatch, filtersString]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading regret dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description="Failed to load regret dashboard data. Please try again."
        type="error"
        showIcon
        style={{ margin: 20 }}
      />
    );
  }

 
  if (!stats || stats.length === 0) {
    return (
      <Alert
        message="No Data Available"
        description="No regret dashboard data available for the selected filters."
        type="info"
        showIcon
        style={{ margin: 20 }}
      />
    );
  }

  return (
    <>
      <div
        style={{
          background: "#0D9488",
          padding: "12px 20px",
          borderRadius: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={5} style={{ color: "#fff", margin: 0 }}>
          <CloseCircleOutlined style={{ marginRight: 8 }} />
          Regret Case Tracking Dashboard
        </Title>
        {/* <Button
          onClick={onDetailedClick}
          icon={<ExportOutlined />}
          style={{ background: "#fff", color: "#135200" }}
        >
          Detailed View
        </Button> */}
      </div>
      
      <div style={{ background: "#fff", padding: 20 }}>
        {/* Overall Statistics */}
        <Row gutter={[16, 16]}>
          {stats.map((stat, index) => (
            <Col xs={24} sm={12} md={8} key={index}>
              <div
                style={{
                  background: stat.bgColor,
                  color: stat.textColor,
                  borderRadius: 8,
                  padding: 16,
                  height: "100%",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  {React.cloneElement(iconMap[stat.title] || <CloseCircleOutlined />, {
                    style: { color: stat.textColor }
                  })}
                </div>
                <Text style={{ fontWeight: 500, color: stat.textColor }}>
                  {stat.title}
                </Text>
                <Title level={4} style={{ margin: 0, color: stat.textColor }}>
                  {stat.value}
                </Title>
              </div>
            </Col>
          ))}
        </Row>

        {/* Common Regret Reasons */}
        {commonReasons && commonReasons.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <Title level={5}>Common Regret Reasons</Title>
            <Row gutter={[16, 16]} align="stretch">
              {commonReasons.map((item, index) => (
                <Col xs={24} sm={12} key={index}>
                  <div
                    style={{
                      background: "#fafafa",
                      borderRadius: 8,
                      padding: "12px 16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      height: "100%", 
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Tag color="red" style={{ marginRight: 8 }}>
                        {index + 1}
                      </Tag>
                      <Text>{item.label}</Text>
                    </div>
                    <Text strong>
                      {item.count} ({item.percent})
                    </Text>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Footer KPIs */}
        {kpis && (
          <div
            style={{
              marginTop: 40,
              display: "flex",
              gap: 16,
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(156,244,228, 0.2)",
                borderRadius: 8,
                padding: 16,
                textAlign: "center",
              }}
            >
              <Text style={{ color: "#1b7275", fontWeight: 500 }}>
                Total Cases Tracked
              </Text>
              <Title level={3} style={{ margin: 0, color: "#1b7275" }}>
                {kpis.totalCasesTracked}
              </Title>
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: "rgba(156,244,228, 0.2)",
                borderRadius: 8,
                padding: 16,
                textAlign: "center",
              }}
            >
              <Text style={{ color: "#1b7275", fontWeight: 500 }}>
                Resolution Rate
              </Text>
              <Title level={3} style={{ margin: 0, color: "#1b7275" }}>
                {kpis.resolutionRate}
              </Title>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RegretDashboard;